import { themeAtom } from "../../shared/atoms";
import AdminDebugEmptyPanel from "../components/debug/AdminDebugEmptyPanel";
import CronJobCard from "../components/debug/CronJobCard";
import CronJobsPanel from "../components/debug/CronJobsPanel";
import { ADMIN_CRON_JOBS, AdminCronJob } from "../models/admin-cron.models";
import { ADMIN_DEBUG_SECTIONS, AdminDebugSectionId } from "../models/admin.models";
import { adminStyles } from "../styles/admin.styles";
import { useAtomValue } from "jotai";
import { twMerge } from "tailwind-merge";

interface Props {
  section: Exclude<AdminDebugSectionId, "techFormInbox" | "onboarding" | "voyagePnl">;
}

const TECH_FORM_DEBUG_JOB: AdminCronJob = {
  id: "offline-tech-form-debug",
  group: "Forms",
  title: "Offline Tech Form",
  purpose: "Generates and emails offline Tech Form payloads using the current signed-in token.",
  outputFocus: "Generated TF_OFFLINE_ID, HTML output, email delivery, and payload summaries.",
  params: [
    {
      key: "imoNumber",
      label: "IMO number",
      type: "text",
      placeholder: "9875642",
      helper: "Required for generation and sending.",
    },
    {
      key: "email",
      label: "Recipient email",
      type: "email",
      placeholder: "vessel.master@example.com",
      helper: "Must match the vessel-master email on the target Tech Form request.",
    },
    {
      key: "useMocks",
      label: "Use mocks",
      type: "checkbox",
      helper: "Uses mock Snowflake payloads for local generation.",
    },
  ],
  variants: [
    {
      id: "generate",
      label: "Generate HTML",
      endpoint: "generate-offline-tech-form-dev",
      risk: "verification",
      description: "Generates the offline HTML and assigns a TF_OFFLINE_ID without sending email.",
      paramKeys: ["imoNumber", "useMocks"],
      requiredParamKeys: ["imoNumber"],
    },
    {
      id: "send",
      label: "Generate and send",
      endpoint: "generate-offline-tech-form-and-send-email-dev",
      risk: "verification",
      description: "Generates a fresh offline Tech Form and sends it to the configured recipient.",
      paramKeys: ["imoNumber", "email", "useMocks"],
      requiredParamKeys: ["imoNumber", "email"],
    },
  ],
};

const REJOIN_FLEET_TECH_FORM_JOB = ADMIN_CRON_JOBS.find((job) => job.id === "reset-tech-forms-on-new-time-charter");

/**
 * Admin debug tooling starts with cron controls and leaves room for feature-specific tools.
 */
const AdminDebugPage = ({ section }: Props) => {
  const theme = useAtomValue(themeAtom);
  const debugSection = ADMIN_DEBUG_SECTIONS.find((item) => item.id === section) ?? ADMIN_DEBUG_SECTIONS[0];

  if (section === "cronJobs") {
    return (
      <main data-test="admin-debug-page" className={adminStyles.page.content}>
        <CronJobsPanel />
      </main>
    );
  }

  return (
    <main
      data-test="admin-debug-page"
      className={twMerge("min-h-0 flex-1 overflow-auto", adminStyles.page.pagePadding)}
    >
      <div className="mb-6">
        <p className={twMerge("text-xs font-semibold uppercase text-green")}>Debug</p>
        <h1 className={twMerge("mt-1", adminStyles.heading.pageTitle, theme.home.tailwind.textDefault)}>
          {debugSection.label}
        </h1>
        <p className={twMerge("mt-2", adminStyles.heading.body, theme.uwhcAssistant.textOutput)}>
          Operational admin actions for manual checks, dry runs, and controlled production cron execution.
        </p>
      </div>

      {section === "techForm" && (
        <div className="grid gap-6 xl:grid-cols-2" data-test="admin-tech-form-debug-tools">
          <CronJobCard job={TECH_FORM_DEBUG_JOB} />
          {REJOIN_FLEET_TECH_FORM_JOB && <CronJobCard job={REJOIN_FLEET_TECH_FORM_JOB} />}
        </div>
      )}
      {section === "balticQuestionnaire" && <AdminDebugEmptyPanel title="Baltic Questionnaire" />}
    </main>
  );
};

export default AdminDebugPage;
